import { useCallback, useEffect, useState } from 'react'
import { useAuth } from 'src/contexts/auth'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { fileToBase64 } from 'src/utils/file'
import { useFormContext } from 'react-hook-form'
import ROUTES from 'src/constants/routes'

import { useUpdateAnimalImage, useCreateAnimalComplete } from 'src/hooks/api/animal'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { validateAnimalWeight, WeightValidationResult } from 'src/utils/weightValidation'
import { useNavigate } from 'react-router-dom'
import { scrollToTop } from '../utils/functions'
import { AnimalRegistrationForm } from '..'

export const useCreateAnimal = () => {
  const navigate = useNavigate()
  const { userFarm: farmId } = useAuth()

  const [createContinuosAnimal, setCreateContinuosAnimal] = useState(false)
  const [animalImage, setAnimalImage] = useState<File | null>(null)
  const [weightValidationResult, setWeightValidationResult] = useState<WeightValidationResult | null>(null)
  const [showWeightWarning, setShowWeightWarning] = useState(false)
  const [pendingAnimalData, setPendingAnimalData] = useState<CreateAnimalForm | null>(null)

  const { handleSubmit, reset, setError, watch, setValue, clearErrors } = useFormContext<CreateAnimalForm>()

  const { mutateAsync: createAnimalCompleteAsync, isPending: isCreatingAnimalComplete } = useCreateAnimalComplete()
  const { mutateAsync: _updateAnimalImage } = useUpdateAnimalImage('createAnimalImage')

  const animalType = watch('tipoAnimal')

  useEffect(() => {
    if (animalType !== 'animal_comprado') {
      setValue('fazendaOrigem', null)
      clearErrors('fazendaOrigem')
    }
  }, [animalType, setValue, clearErrors])

  const createAnimalProcess = useCallback(
    async (formData: AnimalRegistrationForm) => {
      const animalDataForm = {
        ...formData,
        idFazenda: farmId!,
        tourosMultiplos: Array.isArray(formData.tourosMultiplos) ? formData.tourosMultiplos.map((t) => t.id) : []
      }

      await createAnimalCompleteAsync(animalDataForm)

      if (showWeightWarning) {
        setShowWeightWarning(false)
      }

      toastSyncSuccess('Animal criado com sucesso!')

      reset()

      if (createContinuosAnimal) {
        scrollToTop()
        return
      }
      navigate(ROUTES.ANIMAL.ROOT)
    },
    [farmId, createAnimalCompleteAsync, reset, createContinuosAnimal, navigate, showWeightWarning]
  )

  const onSubmit = handleSubmit(async (formData) => {
    try {
      if (formData.pesoAtual && formData.dataNascimento && formData.sexo) {
        const pesoStr = String(formData.pesoAtual)

        const weight =
          typeof formData.pesoAtual === 'string' ? parseFloat(pesoStr.replace(',', '.')) : parseFloat(pesoStr)

        const validationResult = validateAnimalWeight(weight, formData.dataNascimento, formData.sexo)

        if (validationResult.status === 'blocked' || validationResult.status === 'warning') {
          setWeightValidationResult(validationResult)
          setPendingAnimalData(formData)
          setShowWeightWarning(true)
          return
        }
      }
      await createAnimalProcess(formData as AnimalRegistrationForm)
    } catch (e) {
      toast.error('Erro ao criar animal. Tente novamente!')
    }
  })

  const handleSaveImage = useCallback(
    async (AnimalId: string): Promise<void> => {
      if (!animalImage) return
      const base64 = await fileToBase64(animalImage)
      try {
        await _updateAnimalImage({
          farmId: farmId!,
          animalId: AnimalId,
          imageBase64: base64
        })
      } catch (error) {}
    },
    [animalImage, farmId]
  )

  const handleConfirmWeightWarning = useCallback(async () => {
    if (pendingAnimalData) {
      await createAnimalProcess(pendingAnimalData as AnimalRegistrationForm)

      setPendingAnimalData(null)
      setWeightValidationResult(null)
    }
  }, [pendingAnimalData, createAnimalProcess])

  const handleCancelWeightWarning = useCallback(() => {
    if (weightValidationResult?.status === 'blocked' && weightValidationResult.details) {
      const { ageInMonths, minLimit, maxLimit } = weightValidationResult.details
      setError('pesoAtual', {
        type: 'manual',
        message: `Com ${ageInMonths} meses o peso deve estar entre ${minLimit}kg e ${maxLimit}kg`
      })
    }
    setPendingAnimalData(null)
    setWeightValidationResult(null)
  }, [weightValidationResult, setError])

  return {
    createContinuosAnimal,
    animalImage,
    onSubmit,
    setCreateContinuosAnimal,
    setAnimalImage,
    handleSaveImage,
    showWeightWarning,
    weightValidationResult,
    handleConfirmWeightWarning,
    handleCancelWeightWarning,
    isCreatingAnimal: isCreatingAnimalComplete
  }
}
